/**
 * stepDetection — pure predicate builders for DO-IT-TO-ADVANCE journey steps
 * (walkthrough UX option C, Connor pick 2026-07-21: "B, C, D").
 *
 * A step that declares `verifyComplete` stops showing a manual CTA: the
 * overlay polls the predicate (~800ms) and auto-advances with a success flash
 * the moment it returns true — the user's REAL action is what completes the
 * step (Pendo/WalkMe smart-step model). Predicates must be:
 *   - cheap (they poll): DOM reads + store getState reads only, no fetches
 *   - side-effect free
 *   - resilient: return false on anything unexpected, never throw
 *
 * AUTHORING RULE (learned live, 2026-07-22): predicates must match DURABLE
 * state, never transient UI. A toast ("Submitted to ...") is gone before the
 * 700ms poller looks; the persistent badge ("awaiting approval"), a filled
 * cell, or a store value is what survives. If the only signal is transient,
 * find the durable state it leaves behind — there always is one.
 *
 * Builders below cover the v1 "money step" detections:
 *   textVisible    — a regex matches visible page text (e.g. /Submitted to/)
 *   routeIs        — the SPA is on a given path prefix
 *   anyCellFilled  — the week grid holds at least one hours value
 *   personaChanged — the acting persona differs from a snapshot taken when
 *                    the step activated (for "switch persona" hand-off steps)
 *   all/any        — combinators
 */
import { useAppStore } from '@/store/appStore'

export type StepPredicate = () => boolean

/**
 * Visible-text match. Scans the main content AND portaled dialogs (Radix
 * dialogs render outside <main> — the policy gate lives there, which is
 * exactly what the submit step needs to see; caught live 2026-07-22 when the
 * gate fired but the step kept "watching"). The walkthrough's own card is
 * excluded via its data-walkthrough marker so instruction copy that quotes
 * the target phrase can never self-satisfy the predicate.
 */
export function textVisible(re: RegExp): StepPredicate {
  return () => {
    try {
      const roots: HTMLElement[] = []
      const main = document.querySelector('main') ?? document.body
      roots.push(main as HTMLElement)
      document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]').forEach((d) => {
        if (!d.closest('[data-walkthrough]')) roots.push(d)
      })
      return roots.some((r) => re.test(r.innerText ?? ''))
    } catch {
      return false
    }
  }
}

/** SPA route check (prefix match so query strings / sub-paths pass). */
export function routeIs(pathPrefix: string): StepPredicate {
  return () => {
    try {
      return window.location.pathname.startsWith(pathPrefix)
    } catch {
      return false
    }
  }
}

/**
 * At least one week-grid cell carries hours. Reads the grid's committed cell
 * buttons (GridNumberCell renders `data-grid-row`/`data-grid-col` anchors) and
 * accepts any numeric > 0 in their text.
 */
export function anyCellFilled(): StepPredicate {
  return () => {
    try {
      const cells = document.querySelectorAll<HTMLElement>('[data-grid-row][data-grid-col]')
      for (const c of Array.from(cells)) {
        const v = parseFloat((c.innerText || '').replace(/[^\d.]/g, ''))
        if (Number.isFinite(v) && v > 0) return true
      }
      return false
    } catch {
      return false
    }
  }
}

/**
 * Persona hand-off detection. Scenarios are static data, so the predicate
 * can't close over "who you were when the step appeared" — instead the
 * overlay calls `snapshotPersonaForStep()` every time a step ACTIVATES, and
 * `personaChanged()` compares the live identity against that snapshot. True
 * once the acting persona genuinely differs (both sides non-null).
 */
let personaSnapshot: string | null = null

export function snapshotPersonaForStep(): void {
  try {
    personaSnapshot = useAppStore.getState().currentUser?.id ?? null
  } catch {
    personaSnapshot = null
  }
}

export function personaChanged(): StepPredicate {
  return () => {
    try {
      const cur = useAppStore.getState().currentUser?.id ?? null
      return !!cur && !!personaSnapshot && cur !== personaSnapshot
    } catch {
      return false
    }
  }
}

export function all(...preds: StepPredicate[]): StepPredicate {
  return () => preds.every((p) => p())
}

export function any(...preds: StepPredicate[]): StepPredicate {
  return () => preds.some((p) => p())
}
